import { CtaLesson } from 'components/cta-lesson'
import { Layout } from 'components/layout'
import { PgWithControls } from 'components/pg-with-controls'
// import { TmCoachAdPopup } from 'components/tm-coach-ad-popup'
import { LESSON_NAMES } from 'constants/lessons'
import { PG } from 'constants/pg'
import { useLoadInitialData } from 'hooks/database/use-load-initial-data'
import { useAuthUser } from 'next-firebase-auth'
import { useEffect, useState } from 'react'

import { urlWithSiteOrigin } from '../../utils/url-with-site-origin'
import {
  AILabel,
  CoachContainer,
  CoachContent,
  CoachCTA,
  CoachSection,
  CoachSubtitle,
  CoachTitle,
  ContentParagraph,
  FeatureCard,
  FeatureDescription,
  FeatureGrid,
  FeatureIcon,
  FeatureItem,
  FeatureList,
  FeatureSection,
  FeatureText,
  FeatureTitle,
  Hero,
  HeroButton,
  HeroContent,
  HeroSubtitle,
  HeroTitle,
  ImageContainer,
  InlineLink,
  ProActionButton,
  ProCard,
  ProDescription,
  ProIcon,
  ProsGrid,
  ProsSection,
  ProsTitle,
  ProTitle,
  SummaryCard,
} from './home.styles'
import { featureCardsData, prosCardsData, TypingCoachFeatureItems } from './resources'

export const Home = () => {
  const title = 'Typing Practice - Free Typing Learning with Speed-Up Lessons'
  const metaDescription =
    'The perfect Website to Learn touch typing with 36+ free, engaging lessons. Practice keyboarding skills & boost your WPM speed. Start typing practice now!'

  const [typingSpeed, setTypingSpeed] = useState(0)

  useLoadInitialData()

  const authUser = useAuthUser()
  const authLoading = !authUser.clientInitialized

  // Simple effect to animate typing speed counter
  useEffect(() => {
    const timer = setInterval(() => {
      setTypingSpeed((prev) => {
        if (prev < 100) return prev + 1
        clearInterval(timer)
        return 100
      })
    }, 30)

    return () => clearInterval(timer)
  }, [])

  return (
    <Layout
      authLoading={authLoading}
      uid={authUser.id}
      signOut={authUser.signOut}
      seoProps={{
        title,
        description: metaDescription,
        canonicalUrl: urlWithSiteOrigin(''),
        pageUrl: urlWithSiteOrigin(''),
      }}
    >
      {/* PgWithControls at the top of the page */}
      <PgWithControls
        pgName={PG.MAIN_PG}
        defaultRules={{
          lessonName: LESSON_NAMES.PARAGRAPH,
        }}
      />

      {/* Hero Section with H1 */}
      <Hero>
        <HeroContent>
          <HeroTitle as="h1">Typing Practice - Learn Touch Typing Free</HeroTitle>
          <HeroSubtitle>
            Learn to type {typingSpeed}+ WPM without looking at the keyboard. Free online practice with real-time
            analytics.
          </HeroSubtitle>
          <HeroButton href="/typing-practice" title="Begin your free typing practice">
            Start Typing Practice
          </HeroButton>
        </HeroContent>
      </Hero>

      {/* Pros Section with H2 and H3s */}
      <ProsSection>
        <ProsTitle as="h2">8 Pros of Typing Practice in Typing Mentor</ProsTitle>
        <ProsGrid>
          {prosCardsData.map((card) => (
            <ProCard key={card.id}>
              <ProIcon>{card.icon}</ProIcon>
              {/* Cards 1-6 get H3, cards 7-8 get H2 */}
              {card.id <= 6 ? <ProTitle as="h3">{card.title}</ProTitle> : <ProTitle as="h2">{card.title}</ProTitle>}
              <ProDescription>
                {card.description}
                {card.readMoreLink && (
                  <InlineLink href={card.readMoreLink} title={`Read more about ${card.title}`}>
                    Read More
                  </InlineLink>
                )}
              </ProDescription>

              {card.lesson && (
                <ProActionButton>
                  <CtaLesson
                    cta={card.lesson}
                    href={card.actionLink}
                    title={card.title}
                    blank={false}
                    buttonView={false}
                  >
                    {card.actionText}
                  </CtaLesson>
                </ProActionButton>
              )}
            </ProCard>
          ))}
        </ProsGrid>

        {/* Summary Card with H2 */}
        <SummaryCard>
          <ContentParagraph>
            Practice a lot, if possible every day for 15-20 minutes. Track your performance on the
            <InlineLink href="/stats" title="View your typing statistics dashboard">
              dashboard page
            </InlineLink>
            . While practicing you should take typing tests regularly. If you are interested in taking the test and
            knowing more about the benefits of it, visit the
            <InlineLink href="/typing-test" title="Take a typing test to measure your speed and accuracy">
              typing test
            </InlineLink>
            page. Also, you can read our
            <InlineLink href="/articles" title="Read articles about typing practice and improvement">
              articles
            </InlineLink>
            and subscribe to our newsletter to get the latest news and updates directly in your inbox.
          </ContentParagraph>
        </SummaryCard>
      </ProsSection>

      {/* Feature Section without its own primary heading */}
      <FeatureSection>
        <div style={{ marginBottom: '40px' }}>
          <p style={{ fontSize: '1.25rem', textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            Our typing practice platform offers all the tools you need to master touch typing
          </p>
        </div>
        <FeatureGrid>
          {featureCardsData.map((feature) => (
            <FeatureCard key={feature.id}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeatureSection>

      {/* Typing Mentor Coach Section with H3 */}
      <CoachSection>
        <CoachContainer>
          <CoachContent>
            <CoachTitle as="h3">Personalized AI-Powered Coaching</CoachTitle>
            <CoachSubtitle>
              Unlock your full typing potential with our AI-powered typing coach that adapts to your individual needs
              and accelerates your progress.
            </CoachSubtitle>

            <FeatureList>
              {TypingCoachFeatureItems.map((feature, index) => (
                <FeatureItem key={index}>
                  <FeatureText>{feature}</FeatureText>
                </FeatureItem>
              ))}
            </FeatureList>

            <CoachCTA href="/typing-mentor-coach" title="Try our AI-powered typing coach">
              Try Typing Mentor Coach
            </CoachCTA>
          </CoachContent>
          <ImageContainer>
            <AILabel>AI-Powered</AILabel>
          </ImageContainer>
        </CoachContainer>
      </CoachSection>

      {/* Popup Component */}
      {/* <TmCoachAdPopup /> */}
    </Layout>
  )
}
